import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { useToast } from "@/hooks/use-toast";
import { UserPlus, Pencil, Trash2, Users, Calculator, UserCheck, Hammer, RefreshCw, ArrowRight, Activity, Coins, CalendarDays, ArrowUpDown, ArrowUp, ArrowDown, Info, X, Briefcase, ChevronRight } from "lucide-react";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { useCalculations } from "@/hooks/useCalculations";
import { useEquipeStats } from "@/hooks/useEquipeStats";
import EmptyState from "@/components/EmptyState";
import ConfirmDialog from "@/components/ConfirmDialog";
import { labels, placeholders, toasts, emptyStates, tooltips, modals, tables } from "@/lib/content";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import TeamManager from "@/components/team/TeamManager";
import MemberTeamAssignment from "@/components/team/MemberTeamAssignment";
import { BulkMembreModal } from "@/components/team/BulkMembreModal";
import { BulkMembreEditModal } from "@/components/team/BulkMembreEditModal";
import { useSubscription } from "@/contexts/SubscriptionContext";
import { ModalRetraitChantier } from "@/components/equipe/ModalRetraitChantier";
import { ModalAffectationChantier } from "@/components/equipe/ModalAffectationChantier";
import { MiroAnnotations } from "@/components/annotations/MiroAnnotations";
import { ModalAffectationChantierBulk } from "@/components/equipe/ModalAffectationChantierBulk";
import { ModalRetraitChantierBulk } from "@/components/equipe/ModalRetraitChantierBulk";
import { ModalDateFinStatut, ModalConfirmChangementAnticipe } from "@/components/equipe/ModalDateFinStatut";
import { MemberDetailModal } from "@/components/equipe/MemberDetailModal";
import { ModalCoutJourBreakdown } from "@/components/equipe/ModalCoutJourBreakdown";
import { useRetraitChantier } from "@/hooks/useRetraitChantier";
import { useAffectationChantier } from "@/hooks/useAffectationChantier";
import { cn } from "@/lib/utils";
import { usePlanningStore } from "@/stores/planningStore";
import { TipsModal } from "@/components/tips/TipsModal";
import { isAffectedForTeamsPage } from "@/lib/disponibilite-utils";
import { usePeriodSync } from "@/hooks/usePeriodSync";
import type { JourDetail } from "@/types/calendar";

interface Membre {
  id: string;
  prenom: string;
  nom: string;
  poste: string | null;
  specialite: string | null;
  taux_horaire: number;
  charges_patronales: number;
  coefficient_charges: number;
  heures_par_jour: number;
  statut: 'sur_chantier' | 'disponible' | 'repos' | 'en_arret' | 'en_rotation';
  equipe_id?: string | null;
  date_fin_statut?: string | null;
}

interface Equipe {
  id: string;
  nom: string;
  specialite: string;
}

const Team = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const { openPanel, setActiveTab, openPlanningToday } = usePlanningStore();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  // Hook de synchronisation de période (13h, 17h, minuit)
  // refreshKey change automatiquement quand la période change
  const { refreshKey } = usePeriodSync();

  // Hook de subscription pour les limites du plan
  const { limits } = useSubscription();

  // État pour le TipsModal (conseils depuis la checklist)
  const [showTips, setShowTips] = useState(false);
  // État pour le modal bulk d'ajout de membres
  const [bulkAddMemberOpen, setBulkAddMemberOpen] = useState(false);
  const [membres, setMembres] = useState<Membre[]>([]);
  const [equipes, setEquipes] = useState<Equipe[]>([]);
  // Ancien dialog supprimé - remplacé par BulkMembreEditModal (#3)
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [entrepriseId, setEntrepriseId] = useState<string | null>(null);
  // editingMembre supprimé - remplacé par membresToEdit (#3)
  const [filterStatut, setFilterStatut] = useState<'all' | 'sur_chantier' | 'disponible' | 'repos' | 'en_arret'>('all');
  const [filterEquipe, setFilterEquipe] = useState<string | null>(null);
  // Filtre Métier/Spécialité (#3)
  const [filterSpecialite, setFilterSpecialite] = useState<string | null>(null);
  const [selectedMembres, setSelectedMembres] = useState<Set<string>>(new Set());
  const [bulkEquipeDialogOpen, setBulkEquipeDialogOpen] = useState(false);
  const [bulkStatutDialogOpen, setBulkStatutDialogOpen] = useState(false);
  // Modal d'édition bulk (#3)
  const [bulkEditModalOpen, setBulkEditModalOpen] = useState(false);
  const [membresToEdit, setMembresToEdit] = useState<Membre[]>([]);
  // Modal d'affectation bulk (#2)
  const [bulkAffectationModalOpen, setBulkAffectationModalOpen] = useState(false);
  // Modal d'affectation bulk depuis "Changer statut → Sur chantier" (#6)
  const [bulkAffectationFromStatutOpen, setBulkAffectationFromStatutOpen] = useState(false);
  const [membresForBulkAffectation, setMembresForBulkAffectation] = useState<{ id: string; prenom: string; nom: string; poste?: string; taux_horaire?: number; equipe_id?: string }[]>([]);
  // Modal de retrait bulk (#6)
  const [retraitBulkModalOpen, setRetraitBulkModalOpen] = useState(false);
  const [membresForRetrait, setMembresForRetrait] = useState<{ id: string; prenom: string; nom: string }[]>([]);
  const [nouveauStatutRetraitBulk, setNouveauStatutRetraitBulk] = useState<'disponible' | 'repos' | 'en_arret'>('disponible');
  // Record des affectations actives pour le modal retrait bulk (membreId → affectation avec détails)
  const [affectationsForRetraitBulk, setAffectationsForRetraitBulk] = useState<Record<string, { id: string; chantier_id: string; chantier_nom: string; date_debut: string; date_fin: string; heures_par_jour: number | null }>>({});
  // États pour la modal de retrait du chantier
  const [modalRetraitOpen, setModalRetraitOpen] = useState(false);
  const [membreARetirer, setMembreARetirer] = useState<Membre | null>(null);
  const [affectationsARetirer, setAffectationsARetirer] = useState<any[]>([]);
  const [nouveauStatutRetrait, setNouveauStatutRetrait] = useState<Membre["statut"]>('disponible');
  // États pour la modal d'affectation au chantier
  const [modalAffectationOpen, setModalAffectationOpen] = useState(false);
  const [membreAAfecter, setMembreAAfecter] = useState<Membre | null>(null);
  const [chantiersDisponibles, setChantiersDisponibles] = useState<any[]>([]);
  const [affectationsMembreActuel, setAffectationsMembreActuel] = useState<any[]>([]);
  // États pour la modal de rotation (flow en 2 étapes : retrait puis affectation)
  const [isRotationMode, setIsRotationMode] = useState(false);
  const [membreEnRotation, setMembreEnRotation] = useState<Membre | null>(null);
  // États pour la modal de date fin de statut (repos/arrêt)
  const [modalDateFinStatutOpen, setModalDateFinStatutOpen] = useState(false);
  const [membrePourDateFinStatut, setMembrePourDateFinStatut] = useState<Membre | null>(null);
  const [nouveauStatutPourDateFin, setNouveauStatutPourDateFin] = useState<'repos' | 'en_arret'>('repos');
  // États pour la confirmation de changement anticipé
  const [modalConfirmAnticipeOpen, setModalConfirmAnticipeOpen] = useState(false);
  const [membrePourConfirmAnticipe, setMembrePourConfirmAnticipe] = useState<Membre | null>(null);
  const [nouveauStatutAnticipe, setNouveauStatutAnticipe] = useState<Membre["statut"]>('disponible');
  // État pour les affectations actives (pour affichage dans le tableau)
  // Contient le nom du chantier, la date de fin, et si c'est un retard
  const [affectationsActives, setAffectationsActives] = useState<Record<string, { chantier: string; dateFin: string; isRetard: boolean }>>({});

  // État pour la dernière affectation de chaque membre (en cours ou terminée) - pour le HoverCard
  const [dernieresAffectations, setDernieresAffectations] = useState<Record<string, {
    chantier: string;
    dateDebut: string;
    dateFin: string;
    jours: number;
    isEnCours: boolean;
  }>>({});

  // État pour la fiche détail membre (#4)
  const [memberDetailOpen, setMemberDetailOpen] = useState(false);
  const [selectedMemberForDetail, setSelectedMemberForDetail] = useState<Membre | null>(null);

  // État pour le modal breakdown coût aujourd'hui (#1c)
  const [coutBreakdownModalOpen, setCoutBreakdownModalOpen] = useState(false);

  // États pour le tri des colonnes
  type SortColumn = 'nom' | 'poste' | 'specialite' | 'taux_horaire' | 'cout_journalier' | 'statut' | null;
  type SortDirection = 'asc' | 'desc';
  const [sortColumn, setSortColumn] = useState<SortColumn>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  // Détection arrivée depuis la checklist pour afficher le TipsModal + scroll en haut
  useEffect(() => {
    const fromChecklist = (location.state as any)?.fromChecklist;
    const tipsSeen = localStorage.getItem('b8ild_tips_equipe_seen');

    if (fromChecklist) {
      // Scroll en haut de page si on vient de la checklist
      window.scrollTo({ top: 0, behavior: 'instant' });

      if (!tipsSeen) {
        setShowTips(true);
      }
    }
  }, [location.state]);

  // Gestion des paramètres URL pour ouvrir le planning automatiquement
  // + filtre statut depuis URL (ex: ?status=sur_chantier)
  useEffect(() => {
    const view = searchParams.get('view');
    const dateParam = searchParams.get('date');
    const statusParam = searchParams.get('status');

    if (view === 'planning') {
      if (dateParam === 'today') {
        // Ouvrir le planning à la date d'aujourd'hui
        openPlanningToday();
      } else {
        // Ouvrir le planning simple
        openPanel();
        setActiveTab('planning');
      }
    }

    // Appliquer le filtre statut depuis l'URL
    if (statusParam === 'sur_chantier' || statusParam === 'occupe') {
      setFilterStatut('sur_chantier');
    } else if (statusParam === 'disponible') {
      setFilterStatut('disponible');
    } else if (statusParam === 'repos') {
      setFilterStatut('repos');
    } else if (statusParam === 'en_arret') {
      setFilterStatut('en_arret');
    }
  }, [searchParams, openPlanningToday, openPanel, setActiveTab]);

  // Fermeture du TipsModal et sauvegarde en localStorage
  const handleCloseTips = () => {
    setShowTips(false);
    localStorage.setItem('b8ild_tips_equipe_seen', 'true');
  };

  // Highlight permanent tant que les conditions sont remplies (onboarding UX)
  // - Si 0 équipe → highlight "Créer équipe" (géré dans TeamManager)
  // - Si équipe existe mais 0 membre → highlight "Ajouter membre"
  const shouldHighlightAddMember = equipes.length > 0 && membres.length === 0;

  // Refs pour persister les valeurs entre les re-renders (fix pour le mode rotation)
  const isRotationModeRef = useRef(false);
  const membreEnRotationRef = useRef<Membre | null>(null);
  // formData supprimé - remplacé par BulkMembreEditModal (#3)

  const { cout_journalier_equipe, calculerCoutJournalierMembre } = useCalculations({
    membres: membres.filter(m => m.statut === 'sur_chantier' || m.statut === 'disponible'),
  });

  // Stats d'occupation et coûts de l'équipe
  const equipeStats = useEquipeStats({ entrepriseId, membres });

  // Hook pour le retrait du chantier
  const { retirerDuChantier, isLoading: isRetraitLoading } = useRetraitChantier();
  // Hook pour l'affectation au chantier
  const { affecterAuChantier, isLoading: isAffectationLoading } = useAffectationChantier();

  // Définir les fonctions de chargement AVANT les useEffect qui les utilisent
  const loadEntreprise = async () => {
    try {
      const { data, error } = await supabase
        .from("entreprises")
        .select("id")
        .eq("proprietaire_user_id", user?.id)
        .single();

      if (error) throw error;
      setEntrepriseId(data.id);
    } catch (error: any) {
      console.error("Erreur chargement entreprise:", error);
    }
  };

  const loadMembres = useCallback(async () => {
    if (!entrepriseId) return;
    try {
      const { data, error } = await supabase
        .from("membres_equipe")
        .select("*")
        .eq("entreprise_id", entrepriseId)
        .order("created_at", { ascending: false });

      if (error) throw error;
      setMembres((data || []) as any);
    } catch (error: any) {
      console.error("Erreur chargement membres:", error);
    }
  }, [entrepriseId]);

  const loadEquipes = useCallback(async () => {
    if (!entrepriseId) return;
    try {
      const { data, error } = await supabase
        .from("equipes")
        .select("id, nom, specialite")
        .eq("entreprise_id", entrepriseId)
        .order("nom");

      if (error) throw error;
      setEquipes(data || []);
    } catch (error: any) {
      console.error("Erreur chargement équipes:", error);
    }
  }, [entrepriseId]);

  // Charger les affectations actives pour tous les membres (chantier + date de fin + isRetard)
  // Prend en compte les demi-journées (AM/PM) via jours_details
  const loadAffectationsActives = useCallback(async () => {
    if (!entrepriseId) return;
    try {
      const today = new Date().toISOString().split('T')[0];

      // Récupérer les chantiers de l'entreprise pour filtrer les affectations
      const { data: chantiersEntreprise } = await supabase
        .from('chantiers')
        .select('id')
        .eq('entreprise_id', entrepriseId);

      const chantierIds = (chantiersEntreprise || []).map(c => c.id);

      if (chantierIds.length === 0) {
        setAffectationsActives({});
        return;
      }

      // Charger les affectations actives pour les chantiers de l'entreprise
      const { data, error } = await supabase
        .from('affectations_chantiers')
        .select('membre_equipe_id, date_debut, date_fin, notes, jours_details, chantier_id, chantiers(reference_chantier, nom_chantier)')
        .in('chantier_id', chantierIds)
        .gte('date_fin', today);

      if (error) throw error;

      // Créer un mapping membre_id -> { chantier, dateFin, isRetard } (prendre la plus proche si plusieurs)
      // Filtre les affectations selon la période actuelle (AM/PM/OFF après 17h)
      const mapping: Record<string, { chantier: string; dateFin: string; isRetard: boolean }> = {};
      (data || []).forEach((aff: any) => {
        const joursDetails = aff.jours_details as JourDetail[] | null;
        const isRetard = aff.notes?.startsWith('Retard chantier') || false;

        // Vérifier que l'affectation a commencé
        if (aff.date_debut > today) {
          return; // Affectation pas encore commencée
        }

        // Pour TOUTES les affectations, vérifier la période actuelle (AM/PM/OFF)
        // Cela gère : matin terminé à 13h, après-midi/journée terminée à 17h
        if (!isAffectedForTeamsPage(joursDetails, today)) {
          return; // Pas affecté pour la période actuelle
        }

        if (!mapping[aff.membre_equipe_id] || aff.date_fin < mapping[aff.membre_equipe_id].dateFin) {
          const chantierInfo = aff.chantiers;
          const chantierNom = chantierInfo?.reference_chantier || chantierInfo?.nom_chantier || 'Chantier';
          mapping[aff.membre_equipe_id] = {
            chantier: chantierNom,
            dateFin: aff.date_fin,
            isRetard,
          };
        }
      });

      setAffectationsActives(mapping);
    } catch (error: any) {
      console.error("[Team] Erreur chargement affectations:", error);
    }
  }, [entrepriseId]);

  // Charger la dernière affectation de chaque membre (en cours ou terminée) - pour le HoverCard
  const loadDernieresAffectations = useCallback(async () => {
    if (!entrepriseId) return;
    try {
      const today = new Date().toISOString().split('T')[0];

      // Récupérer les chantiers de l'entreprise
      const { data: chantiersEntreprise } = await supabase
        .from('chantiers')
        .select('id')
        .eq('entreprise_id', entrepriseId);

      const chantierIds = (chantiersEntreprise || []).map(c => c.id);
      if (chantierIds.length === 0) {
        setDernieresAffectations({});
        return;
      }

      // Charger TOUTES les affectations (triées par date_debut desc) pour trouver la plus récente par membre
      const { data, error } = await supabase
        .from('affectations_chantiers')
        .select('membre_equipe_id, date_debut, date_fin, jours_travailles, chantier_id, chantiers(reference_chantier, nom_chantier)')
        .in('chantier_id', chantierIds)
        .order('date_debut', { ascending: false });

      if (error) throw error;

      // Créer un mapping membre_id -> dernière affectation (prendre la première car triées desc)
      const mapping: Record<string, { chantier: string; dateDebut: string; dateFin: string; jours: number; isEnCours: boolean }> = {};
      (data || []).forEach((aff: any) => {
        // Ne garder que la première (la plus récente) pour chaque membre
        if (!mapping[aff.membre_equipe_id]) {
          const chantierInfo = aff.chantiers;
          const chantierNom = chantierInfo?.reference_chantier || chantierInfo?.nom_chantier || 'Chantier';
          mapping[aff.membre_equipe_id] = {
            chantier: chantierNom,
            dateDebut: aff.date_debut,
            dateFin: aff.date_fin,
            jours: aff.jours_travailles || 0,
            isEnCours: aff.date_fin >= today && aff.date_debut <= today,
          };
        }
      });

      setDernieresAffectations(mapping);
    } catch (error: any) {
      console.error("[Team] Erreur chargement dernières affectations:", error);
    }
  }, [entrepriseId]);

  useEffect(() => {
    if (user) {
      loadEntreprise();
    }
  }, [user]);

  // Rechargement des données quand entrepriseId change OU quand la période change (13h, 17h, minuit)
  useEffect(() => {
    if (entrepriseId) {
      loadMembres();
      loadEquipes();
      loadAffectationsActives();
      loadDernieresAffectations();
    }
  }, [entrepriseId, loadMembres, loadEquipes, loadAffectationsActives, loadDernieresAffectations, refreshKey]);

  // Écouter les événements de mise à jour des chantiers pour synchroniser les données
  useEffect(() => {
    const handleMetricsUpdate = () => {
      loadAffectationsActives();
    };

    window.addEventListener('chantier-metrics-updated', handleMetricsUpdate);
    return () => window.removeEventListener('chantier-metrics-updated', handleMetricsUpdate);
  }, [loadAffectationsActives]);

  // Vérifier et corriger les statuts incohérents (membres sur_chantier/en_rotation sans affectations)
  // ET vérifier les membres en repos/arrêt dont la date_fin_statut est passée
  useEffect(() => {
    const verifierEtCorrigerStatuts = async () => {
      if (!entrepriseId || membres.length === 0) return;

      const today = new Date().toISOString().split('T')[0];
      let needReload = false;

      // 1. Vérifier les membres repos/en_arret dont la date_fin_statut est passée
      const membresAvecDateFinPassee = membres.filter(
        m => (m.statut === 'repos' || m.statut === 'en_arret') &&
             m.date_fin_statut &&
             m.date_fin_statut <= today
      );

      if (membresAvecDateFinPassee.length > 0) {
        const { error } = await supabase
          .from('membres_equipe')
          .update({ statut: 'disponible', date_fin_statut: null })
          .in('id', membresAvecDateFinPassee.map(m => m.id));

        if (!error) {
          needReload = true;
        }
      }

      // 2. Trouver les membres qui ont un statut "sur_chantier" ou "en_rotation"
      const membresAVerifier = membres.filter(
        m => m.statut === 'sur_chantier' || m.statut === 'en_rotation'
      );

      if (membresAVerifier.length > 0) {
        // Vérifier leurs affectations actives
        const { data: affectations } = await supabase
          .from('affectations_chantiers')
          .select('membre_equipe_id')
          .in('membre_equipe_id', membresAVerifier.map(m => m.id))
          .gte('date_fin', today);

        const membresAvecAffectation = new Set(affectations?.map(a => a.membre_equipe_id) || []);

        // Trouver les membres sans affectation active
        const membresSansAffectation = membresAVerifier.filter(
          m => !membresAvecAffectation.has(m.id)
        );

        // Les remettre en "disponible"
        if (membresSansAffectation.length > 0) {
          const { error } = await supabase
            .from('membres_equipe')
            .update({ statut: 'disponible' })
            .in('id', membresSansAffectation.map(m => m.id));

          if (!error) {
            needReload = true;
          }
        }
      }

      // Recharger une seule fois si nécessaire
      if (needReload) {
        loadMembres();
      }
    };

    verifierEtCorrigerStatuts();
  }, [entrepriseId, membres.length]); // Déclencher quand les membres changent

  // Debounce Realtime — quand l'onglet reprend le focus, Supabase rejoue les events
  // accumulés → sans debounce, loadMembres/loadEquipes tiraient 4 fois → re-renders en rafale
  // → les modaux ouverts (ex: ModalAffectationChantier) peuvent se fermer ou perdre la saisie
  const realtimeDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const debouncedRealtimeRefresh = useCallback(() => {
    if (realtimeDebounceRef.current) {
      clearTimeout(realtimeDebounceRef.current);
    }
    realtimeDebounceRef.current = setTimeout(() => {
      if (import.meta.env.DEV) console.log('[Team] Realtime refresh (debouncé)');
      loadEquipes();
      loadMembres();
      loadAffectationsActives();
    }, 500);
  }, [loadEquipes, loadMembres, loadAffectationsActives]);

  // Cleanup du timeout au démontage
  useEffect(() => {
    return () => {
      if (realtimeDebounceRef.current) clearTimeout(realtimeDebounceRef.current);
    };
  }, []);

  // Subscriptions Realtime pour équipes, affectations et membres
  useEffect(() => {
    if (!entrepriseId) return;

    const channel = supabase
      .channel(`team_page:${entrepriseId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'equipes',
          filter: `entreprise_id=eq.${entrepriseId}`,
        },
        debouncedRealtimeRefresh
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'affectations_chantiers',
        },
        debouncedRealtimeRefresh
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'suspensions_chantiers',
        },
        debouncedRealtimeRefresh
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'membres_equipe',
          filter: `entreprise_id=eq.${entrepriseId}`,
        },
        debouncedRealtimeRefresh
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [entrepriseId, debouncedRealtimeRefresh]);

  // handleSubmit supprimé - remplacé par BulkMembreEditModal (#3)

  const handleEdit = (membre: Membre) => {
    // Utiliser le nouveau modal d'édition bulk (#3)
    setMembresToEdit([membre]);
    setBulkEditModalOpen(true);
  };

  // Ouvrir le modal d'édition avec les membres sélectionnés (#3)
  const handleBulkEdit = () => {
    const membresSelectionnes = membres.filter(m => selectedMembres.has(m.id));
    if (membresSelectionnes.length > 0) {
      setMembresToEdit(membresSelectionnes);
      setBulkEditModalOpen(true);
    }
  };

  const handleDelete = async (id: string) => {
    setDeleteId(id);
    setConfirmOpen(true);
  };

  const confirmDelete = async () => {
    if (!deleteId) return;

    try {
      const { error } = await supabase
        .from("membres_equipe")
        .delete()
        .eq("id", deleteId);

      if (error) throw error;
      toast({ title: "Membre supprimé", description: "Le membre a été supprimé définitivement." });
      loadMembres();
    } catch (error: any) {
      toast({ title: "Erreur", description: toasts.errorGeneric, variant: "destructive" });
    } finally {
      setConfirmOpen(false);
      setDeleteId(null);
    }
  };

  // resetForm supprimé - remplacé par BulkMembreEditModal (#3)

  // Fonction pour changer le tri
  const handleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      // Si même colonne, inverser la direction ou désactiver le tri
      if (sortDirection === 'asc') {
        setSortDirection('desc');
      } else {
        setSortColumn(null);
        setSortDirection('asc');
      }
    } else {
      // Nouvelle colonne, tri ascendant
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  // Fonction helper pour calculer le statut RÉEL basé sur les affectations actives
  // Un membre est "sur_chantier" s'il a une affectation active aujourd'hui
  const getStatutReel = useCallback((membre: Membre): Membre["statut"] => {
    const today = new Date().toISOString().split('T')[0];

    // CAS 1 : Si le membre a une affectation active pour la période actuelle → sur_chantier
    if (affectationsActives[membre.id]) {
      return 'sur_chantier';
    }

    // CAS 2 : Si le statut stocké est "sur_chantier" ou "en_rotation" mais pas d'affectation active → disponible
    if (membre.statut === 'sur_chantier' || membre.statut === 'en_rotation') {
      return 'disponible';
    }

    // CAS 3 : Si le membre est en repos/en_arret ET que sa date_fin_statut est passée → disponible
    if ((membre.statut === 'repos' || membre.statut === 'en_arret') && membre.date_fin_statut) {
      if (membre.date_fin_statut <= today) {
        return 'disponible';
      }
    }

    // Sinon, utiliser le statut stocké (repos, en_arret, disponible)
    return membre.statut;
  }, [affectationsActives]);

  // Liste des spécialités uniques pour le filtre (#3)
  const specialitesUniques = useMemo(() => {
    const values = new Set<string>();
    membres.forEach(m => {
      if (m.poste) values.add(m.poste);
      if (m.specialite) values.add(m.specialite);
    });
    return Array.from(values).sort((a, b) => a.localeCompare(b, 'fr'));
  }, [membres]);

  // Détection si des filtres sont actifs (#3)
  const hasActiveFilters = filterStatut !== 'all' || filterEquipe !== null || filterSpecialite !== null;

  // Fonction pour réinitialiser tous les filtres (#3)
  const resetFilters = () => {
    setFilterStatut('all');
    setFilterEquipe(null);
    setFilterSpecialite(null);
  };

  // Reset des filtres si < 10 membres (filtres masqués)
  useEffect(() => {
    if (membres.length < 10) {
      resetFilters();
    }
  }, [membres.length]);

  // Filtrage et tri des membres
  const filteredMembres = membres
    .filter(membre => {
      // Filtre par statut (utilise le statut RÉEL pour le filtrage)
      const statutReel = getStatutReel(membre);
      if (filterStatut !== 'all' && statutReel !== filterStatut) return false;

      // Filtre par équipe
      if (filterEquipe && membre.equipe_id !== filterEquipe) return false;

      // Filtre par spécialité (#3) - match sur poste OU specialite
      if (filterSpecialite && membre.poste !== filterSpecialite && membre.specialite !== filterSpecialite) return false;

      return true;
    })
    .sort((a, b) => {
      // Tri par défaut : par nom d'équipe alphabétiquement
      if (!sortColumn) {
        const equipeA = equipes.find(e => e.id === a.equipe_id)?.nom || 'zzz'; // 'zzz' pour mettre les sans-équipe à la fin
        const equipeB = equipes.find(e => e.id === b.equipe_id)?.nom || 'zzz';
        const equipeComparison = equipeA.localeCompare(equipeB, 'fr');
        // Si même équipe, trier par nom
        if (equipeComparison === 0) {
          return `${a.prenom} ${a.nom}`.localeCompare(`${b.prenom} ${b.nom}`, 'fr');
        }
        return equipeComparison;
      }

      let comparison = 0;
      const direction = sortDirection === 'asc' ? 1 : -1;

      switch (sortColumn) {
        case 'nom':
          comparison = `${a.prenom} ${a.nom}`.localeCompare(`${b.prenom} ${b.nom}`, 'fr');
          break;
        case 'poste':
          comparison = (a.poste || '').localeCompare(b.poste || '', 'fr');
          break;
        case 'specialite':
          comparison = (a.specialite || '').localeCompare(b.specialite || '', 'fr');
          break;
        case 'taux_horaire':
          comparison = a.taux_horaire - b.taux_horaire;
          break;
        case 'cout_journalier':
          const coutA = a.taux_horaire * (a.coefficient_charges || 1.85) * (a.heures_par_jour || 8);
          const coutB = b.taux_horaire * (b.coefficient_charges || 1.85) * (b.heures_par_jour || 8);
          comparison = coutA - coutB;
          break;
        case 'statut':
          comparison = (a.statut || '').localeCompare(b.statut || '', 'fr');
          break;
      }

      return comparison * direction;
    });

  // Calcul des KPIs basés sur les membres filtrés (utilise le statut RÉEL)
  const totalMembres = filteredMembres.length;
  const membresSurChantier = filteredMembres.filter(m => getStatutReel(m) === 'sur_chantier').length;
  const membresDisponibles = filteredMembres.filter(m => getStatutReel(m) === 'disponible').length;
  const membresRepos = filteredMembres.filter(m => getStatutReel(m) === 'repos').length;
  const membresEnArret = filteredMembres.filter(m => getStatutReel(m) === 'en_arret').length;
  const membresActifs = membresSurChantier + membresDisponibles;
  const coutTotalJournalier = filteredMembres
    .filter(m => {
      const statut = getStatutReel(m);
      return statut === 'sur_chantier' || statut === 'disponible';
    })
    .reduce((total, membre) => {
      // Coût journalier = taux_horaire * coefficient_charges * heures_par_jour
      const coutJournalierMembre = membre.taux_horaire * (membre.coefficient_charges || 1.85) * (membre.heures_par_jour || 8);
      return total + coutJournalierMembre;
    }, 0);
  const coutMoyenHoraire = membresActifs > 0 
    ? filteredMembres.filter(m => m.statut === 'sur_chantier' || m.statut === 'disponible').reduce((sum, m) => sum + m.taux_horaire, 0) / membresActifs 
    : 0;

  // Helper pour badges de statut
  const getStatutBadge = (statut: string) => {
    const badges = {
      sur_chantier: { label: "🚧 Sur chantier", className: "bg-green-500 hover:bg-green-600 text-white" },
      disponible: { label: "✅ Disponible", className: "bg-blue-500 hover:bg-blue-600 text-white" },
      repos: { label: "😴 Repos", className: "bg-yellow-500 hover:bg-yellow-600 text-white" },
      en_arret: { label: "🏥 En arrêt", className: "bg-red-500 hover:bg-red-600 text-white" },
      en_rotation: { label: "🔄 En rotation", className: "bg-orange-500 hover:bg-orange-600 text-white" }
    };
    return badges[statut as keyof typeof badges] || badges.disponible;
  };

  // Fonction pour mettre à jour le statut directement (sans vérification)
  const updateStatutDirect = async (membreId: string, statut: Membre["statut"]) => {
    const { error } = await supabase
      .from("membres_equipe")
      .update({ statut })
      .eq("id", membreId);

    if (error) throw error;
    toast({ title: "Statut modifié avec succès" });
    loadMembres();
  };

  const handleStatutChange = async (membreId: string, statut: Membre["statut"]) => {
    try {
      const membre = membres.find(m => m.id === membreId);
      if (!membre) return;

      // CAS 1 : Si passage de "sur_chantier" vers un autre statut
      if (membre.statut === 'sur_chantier' && statut !== 'sur_chantier') {
        // Charger les affectations actives du membre (date_fin >= aujourd'hui)
        const today = new Date().toISOString().split('T')[0];
        const { data: affectations } = await supabase
          .from('affectations_chantiers')
          .select('*, chantiers(nom_chantier)')
          .eq('membre_equipe_id', membreId)
          .gte('date_fin', today);

        if (affectations && affectations.length > 0) {
          // Si c'est une rotation, charger aussi les chantiers disponibles
          let chantiersFiltered: any[] = [];
          if (statut === 'en_rotation') {
            const { data: chantiers } = await supabase
              .from('chantiers')
              .select('id, nom_chantier, reference_chantier, date_debut_prevue, date_fin_estimee, etat_chantier')
              .eq('entreprise_id', entrepriseId);

            chantiersFiltered = (chantiers || []).filter(
              c => c.etat_chantier !== 'termine' && c.etat_chantier !== 'annule'
            );
            // Activer le mode rotation
            setIsRotationMode(true);
            setMembreEnRotation(membre);
            isRotationModeRef.current = true;
            membreEnRotationRef.current = membre;
          }

          // Ouvrir la modal de retrait
          setMembreARetirer(membre);
          setAffectationsARetirer(affectations);
          setChantiersDisponibles(chantiersFiltered);
          setNouveauStatutRetrait(statut);
          setModalRetraitOpen(true);
          return;
        }
      }

      // CAS 2 : Si passage d'un autre statut vers "sur_chantier"
      if (membre.statut !== 'sur_chantier' && statut === 'sur_chantier') {
        // Charger TOUS les chantiers de l'entreprise
        const { data: chantiers } = await supabase
          .from('chantiers')
          .select('id, nom_chantier, reference_chantier, date_debut_prevue, date_fin_estimee, etat_chantier')
          .eq('entreprise_id', entrepriseId);

        // Filtrer côté client pour exclure les terminés/annulés
        const chantiersFiltered = (chantiers || []).filter(
          c => c.etat_chantier !== 'termine' && c.etat_chantier !== 'annule'
        );

        // Charger les affectations existantes du membre (actives)
        const today = new Date().toISOString().split('T')[0];
        const { data: affectationsExistantes } = await supabase
          .from('affectations_chantiers')
          .select('*, chantiers(nom_chantier)')
          .eq('membre_equipe_id', membreId)
          .gte('date_fin', today);

        setMembreAAfecter(membre);
        setChantiersDisponibles(chantiersFiltered);
        setAffectationsMembreActuel(affectationsExistantes || []);
        setModalAffectationOpen(true);
        return;
      }

      // CAS 3 : Si passage vers "en_rotation" (le membre doit être sur un chantier)
      // Flow en 1 seul modal : retrait du chantier actuel + affectation au nouveau chantier
      if (statut === 'en_rotation') {
        // Charger les affectations actives du membre
        const today = new Date().toISOString().split('T')[0];
        const { data: affectations } = await supabase
          .from('affectations_chantiers')
          .select('*, chantiers(nom_chantier)')
          .eq('membre_equipe_id', membreId)
          .gte('date_fin', today);

        if (!affectations || affectations.length === 0) {
          toast({
            title: "Rotation impossible",
            description: "Ce membre n'est actuellement affecté à aucun chantier.",
            variant: "destructive",
          });
          return;
        }

        // Charger TOUS les chantiers de l'entreprise (sans filtre sur etat_chantier)
        const { data: chantiers, error: chantiersError } = await supabase
          .from('chantiers')
          .select('id, nom_chantier, reference_chantier, date_debut_prevue, date_fin_estimee, etat_chantier')
          .eq('entreprise_id', entrepriseId);

        // Filtrer côté client pour exclure les terminés/annulés
        const chantiersFiltered = (chantiers || []).filter(
          c => c.etat_chantier !== 'termine' && c.etat_chantier !== 'annule'
        );
        // Activer le mode rotation et ouvrir la modal
        setIsRotationMode(true);
        setMembreEnRotation(membre);
        // Mettre à jour les refs aussi pour persister entre les re-renders
        isRotationModeRef.current = true;
        membreEnRotationRef.current = membre;
        setMembreARetirer(membre);
        setAffectationsARetirer(affectations);
        setChantiersDisponibles(chantiersFiltered);
        setNouveauStatutRetrait('en_rotation');
        setModalRetraitOpen(true);
        return;
      }

      // CAS 4 : Si le membre actuel est en repos/en_arret avec une date_fin_statut
      // et qu'on veut changer son statut avant cette date -> demander confirmation
      if ((membre.statut === 'repos' || membre.statut === 'en_arret') && membre.date_fin_statut) {
        const dateFinPrevue = new Date(membre.date_fin_statut);
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        if (dateFinPrevue > today) {
          // La date de fin n'est pas encore passée -> confirmation requise
          setMembrePourConfirmAnticipe(membre);
          setNouveauStatutAnticipe(statut);
          setModalConfirmAnticipeOpen(true);
          return;
        }
      }

      // CAS 5 : Si passage vers repos ou en_arret -> demander la date de fin
      if (statut === 'repos' || statut === 'en_arret') {
        setMembrePourDateFinStatut(membre);
        setNouveauStatutPourDateFin(statut);
        setModalDateFinStatutOpen(true);
        return;
      }

      // Sinon changement direct
      await updateStatutDirect(membreId, statut);
    } catch (error: any) {
      toast({
        title: "Erreur",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  // Callback pour confirmer le retrait du chantier
  const handleConfirmRetrait = async (
    retraits: { affectationId: string; heures: number }[],
    nouvelleAffectation?: {
      chantierId: string;
      dateDebut: string;
      dateFin: string;
      heuresJour1: number;
      heuresJoursSuivants: number;
      joursOuvres: number;
    }
  ) => {
    try {
      // 1. Retirer des chantiers (mettre à jour date_fin + heures_dernier_jour)
      for (const r of retraits) {
        await retirerDuChantier({ affectationId: r.affectationId, heuresDernierJour: r.heures });
      }

      // 2. Fermer la modal de retrait
      setModalRetraitOpen(false);
      setMembreARetirer(null);
      setAffectationsARetirer([]);

      // 3. Si nouvelleAffectation est fournie (mode rotation avec sélection du chantier dans le modal)
      if (nouvelleAffectation && membreARetirer) {
        // Créer la nouvelle affectation
        await affecterAuChantier({
          membreId: membreARetirer.id,
          chantierId: nouvelleAffectation.chantierId,
          dateDebut: nouvelleAffectation.dateDebut,
          dateFin: nouvelleAffectation.dateFin,
          heuresJour1: nouvelleAffectation.heuresJour1,
          heuresJoursSuivants: nouvelleAffectation.heuresJoursSuivants,
        });

        // Changer le statut à "sur_chantier"
        await updateStatutDirect(membreARetirer.id, 'sur_chantier');

        // Réinitialiser le mode rotation
        setIsRotationMode(false);
        setMembreEnRotation(null);
        isRotationModeRef.current = false;
        membreEnRotationRef.current = null;

        toast({ title: "Rotation effectuée", description: "Le membre a été transféré sur le nouveau chantier." });
        return;
      }

      // 4. Si on est en mode rotation sans nouvelleAffectation (ancien flow - fallback)
      // Utiliser les refs car les states peuvent avoir été réinitialisés par un re-render
      if (isRotationModeRef.current && membreEnRotationRef.current) {
        // Charger TOUS les chantiers de l'entreprise
        const { data: chantiers } = await supabase
          .from('chantiers')
          .select('id, nom_chantier, reference_chantier, date_debut_prevue, date_fin_estimee, etat_chantier')
          .eq('entreprise_id', entrepriseId);

        // Filtrer côté client pour exclure les terminés/annulés
        const chantiersFiltered = (chantiers || []).filter(
          c => c.etat_chantier !== 'termine' && c.etat_chantier !== 'annule'
        );

        // Ouvrir la modal d'affectation
        setMembreAAfecter(membreEnRotationRef.current);
        setChantiersDisponibles(chantiersFiltered);
        setAffectationsMembreActuel([]); // Plus d'affectations car on vient de les retirer
        setModalAffectationOpen(true);

        return;
      }

      // 5. Si pas en mode rotation, changer le statut du membre normalement
      if (membreARetirer) {
        await updateStatutDirect(membreARetirer.id, nouveauStatutRetrait);
      }
    } catch (error: any) {
      toast({
        title: "Erreur lors du retrait",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  // Callback pour confirmer l'affectation au chantier
  const handleConfirmAffectation = async (
    data: {
      chantierId: string;
      dateDebut: string;
      dateFin: string;
      heuresParJour: number;
    },
    heuresModifiees?: { affectationId: string; heuresAujourdhui: number }[]
  ) => {
    if (!membreAAfecter) return;

    try {
      // 1. Mettre à jour les heures modifiées sur les affectations existantes
      if (heuresModifiees && heuresModifiees.length > 0) {
        for (const hm of heuresModifiees) {
          const { error } = await supabase
            .from('affectations_chantiers')
            .update({ heures_dernier_jour: hm.heuresAujourdhui })
            .eq('id', hm.affectationId);

          if (error) throw error;
        }
      }

      // 2. Créer la nouvelle affectation
      await affecterAuChantier({
        membreId: membreAAfecter.id,
        ...data,
      });

      // 3. Changer le statut à "sur_chantier"
      await updateStatutDirect(membreAAfecter.id, 'sur_chantier');

      // 4. Fermer la modal et nettoyer
      setModalAffectationOpen(false);
      setMembreAAfecter(null);
      setChantiersDisponibles([]);
      setAffectationsMembreActuel([]);

      // 5. Réinitialiser le mode rotation si actif (utiliser ref pour vérifier)
      if (isRotationModeRef.current) {
        setIsRotationMode(false);
        setMembreEnRotation(null);
        isRotationModeRef.current = false;
        membreEnRotationRef.current = null;
        toast({ title: "Rotation effectuée", description: "Le membre a été transféré sur le nouveau chantier." });
      } else {
        toast({ title: "Membre affecté au chantier" });
      }
    } catch (error: any) {
      toast({
        title: "Erreur lors de l'affectation",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  // Callback pour confirmer la date de fin de statut (repos/arrêt)
  const handleConfirmDateFinStatut = async (dateFinStatut: string) => {
    if (!membrePourDateFinStatut) return;

    try {
      const { error } = await supabase
        .from('membres_equipe')
        .update({
          statut: nouveauStatutPourDateFin,
          date_fin_statut: dateFinStatut,
        })
        .eq('id', membrePourDateFinStatut.id);

      if (error) throw error;

      toast({
        title: "Statut modifié",
        description: `${membrePourDateFinStatut.prenom} est maintenant ${nouveauStatutPourDateFin === 'repos' ? 'en repos' : 'en arrêt'} jusqu'au ${new Date(dateFinStatut).toLocaleDateString('fr-FR')}.`,
      });

      setModalDateFinStatutOpen(false);
      setMembrePourDateFinStatut(null);
      loadMembres();
    } catch (error: any) {
      toast({
        title: "Erreur",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  // Callback pour confirmer le changement anticipé de statut
  const handleConfirmChangementAnticipe = async () => {
    if (!membrePourConfirmAnticipe) return;

    try {
      // Réinitialiser la date_fin_statut et changer le statut
      const { error } = await supabase
        .from('membres_equipe')
        .update({
          statut: nouveauStatutAnticipe,
          date_fin_statut: null,
        })
        .eq('id', membrePourConfirmAnticipe.id);

      if (error) throw error;

      toast({
        title: "Statut modifié",
        description: `${membrePourConfirmAnticipe.prenom} est maintenant ${nouveauStatutAnticipe === 'disponible' ? 'disponible' : nouveauStatutAnticipe}.`,
      });

      setModalConfirmAnticipeOpen(false);
      setMembrePourConfirmAnticipe(null);
      loadMembres();
    } catch (error: any) {
      toast({
        title: "Erreur",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  // Changer l'équipe de tous les membres sélectionnés
  const handleBulkEquipeChange = async (equipeId: string | null) => {
    try {
      const { error } = await supabase
        .from("membres_equipe")
        .update({ equipe_id: equipeId })
        .in("id", Array.from(selectedMembres));

      if (error) throw error;

      toast({ title: `${selectedMembres.size} membre(s) assigné(s)` });
      setSelectedMembres(new Set());
      setBulkEquipeDialogOpen(false);
      loadMembres();
    } catch (error: any) {
      toast({ title: "Erreur", description: error.message, variant: "destructive" });
    }
  };

  // Changer le statut de tous les membres sélectionnés
  const handleBulkStatutChange = async (statut: Membre["statut"]) => {
    setBulkStatutDialogOpen(false);

    // CAS 1: Passage à "sur_chantier" → Ouvrir modal d'affectation bulk
    if (statut === 'sur_chantier') {
      const membresForBulk = membres
        .filter((m) => selectedMembres.has(m.id))
        .map((m) => ({
          id: m.id,
          prenom: m.prenom,
          nom: m.nom,
          poste: m.poste,
          taux_horaire: m.taux_horaire,
          equipe_id: m.equipe_id || undefined,
        }));
      setMembresForBulkAffectation(membresForBulk);
      setBulkAffectationFromStatutOpen(true);
      return;
    }

    // CAS 2: Retrait du chantier (sur_chantier → autre) si des membres ont des affectations actives
    // Identifier les membres qui ont une affectation active
    const membresAvecAffectation = Array.from(selectedMembres).filter(
      (id) => affectationsActives[id]
    );

    if (membresAvecAffectation.length > 0 && (statut === 'disponible' || statut === 'repos' || statut === 'en_arret')) {
      try {
        // Charger les détails complets des affectations pour ces membres
        const today = new Date().toISOString().split('T')[0];

        const { data: affectationsDetails, error } = await supabase
          .from('affectations_chantiers')
          .select('id, membre_equipe_id, chantier_id, date_debut, date_fin, heures_par_jour, chantiers(nom_chantier)')
          .in('membre_equipe_id', membresAvecAffectation)
          .gte('date_fin', today)
          .lte('date_debut', today);

        if (error) throw error;

        // Construire le record des affectations pour le modal
        const affectationsRecord: Record<string, { id: string; chantier_id: string; chantier_nom: string; date_debut: string; date_fin: string; heures_par_jour: number | null }> = {};

        (affectationsDetails || []).forEach((aff: any) => {
          // Prendre la première affectation active pour chaque membre
          if (!affectationsRecord[aff.membre_equipe_id]) {
            affectationsRecord[aff.membre_equipe_id] = {
              id: aff.id,
              chantier_id: aff.chantier_id,
              chantier_nom: aff.chantiers?.nom_chantier || 'Chantier inconnu',
              date_debut: aff.date_debut?.split('T')[0] || today,
              date_fin: aff.date_fin?.split('T')[0] || today,
              heures_par_jour: aff.heures_par_jour,
            };
          }
        });

        // Préparer les membres pour le modal
        const membresRetrait = membres
          .filter((m) => membresAvecAffectation.includes(m.id) && affectationsRecord[m.id])
          .map((m) => ({ id: m.id, prenom: m.prenom, nom: m.nom }));

        if (membresRetrait.length > 0) {
          setMembresForRetrait(membresRetrait);
          setAffectationsForRetraitBulk(affectationsRecord);
          setNouveauStatutRetraitBulk(statut as 'disponible' | 'repos' | 'en_arret');
          setRetraitBulkModalOpen(true);
          return;
        }
      } catch (err: any) {
        console.error('Erreur chargement affectations pour retrait:', err);
        toast({ title: "Erreur", description: "Impossible de charger les affectations.", variant: "destructive" });
        return;
      }
    }

    // CAS 3: Membres sans affectation active → mise à jour directe OK
    try {
      const { error } = await supabase
        .from("membres_equipe")
        .update({ statut })
        .in("id", Array.from(selectedMembres));

      if (error) throw error;

      toast({ title: `${selectedMembres.size} membre(s) mis à jour` });
      setSelectedMembres(new Set());
      loadMembres();
    } catch (error: any) {
      toast({ title: "Erreur", description: error.message, variant: "destructive" });
    }
  };

  // Composant pour en-tête de colonne triable
  const SortableHeader = ({ column, label, align = 'left' }: { column: SortColumn; label: string; align?: 'left' | 'right' }) => {
    const isActive = sortColumn === column;
    return (
      <button
        onClick={() => handleSort(column)}
        className={cn(
          "flex items-center gap-1 font-bold hover:text-primary transition-colors",
          align === 'right' && "ml-auto",
          isActive && "text-primary"
        )}
      >
        {label}
        {isActive ? (
          sortDirection === 'asc' ? (
            <ArrowUp className="h-3.5 w-3.5" />
          ) : (
            <ArrowDown className="h-3.5 w-3.5" />
          )
        ) : (
          <ArrowUpDown className="h-3.5 w-3.5 opacity-40" />
        )}
      </button>
    );
  };

  return (
    <div className="space-y-8 animate-fade-up">
      {entrepriseId && (
        <>
          <TeamManager
            entrepriseId={entrepriseId}
            addMemberButton={
              <Button
                size="default"
                className={cn(
                  "gap-1.5 font-semibold bg-gray-200 text-black hover:bg-gray-300",
                  shouldHighlightAddMember && "animate-highlight-pulse-light"
                )}
                aria-label="Ajouter membre"
                title="Ajouter membre"
                onClick={() => {
                  loadEquipes();
                  setBulkAddMemberOpen(true);
                }}
              >
                <UserPlus className="h-4 w-4" aria-hidden="true" />
                Ajouter membre
              </Button>
            }
            onEquipeChange={(newEquipe) => {
              if (newEquipe) {
                // Ajouter directement la nouvelle équipe au state local
                setEquipes(prev => [...prev, newEquipe].sort((a, b) => a.nom.localeCompare(b.nom)));
              } else {
                // Suppression ou modification : recharger la liste
                loadEquipes();
              }
            }}
          />

          {/* Modal bulk ajout membres */}
          <BulkMembreModal
            open={bulkAddMemberOpen}
            onOpenChange={setBulkAddMemberOpen}
            entrepriseId={entrepriseId}
            equipes={equipes}
            onSuccess={() => {
              // Recharger explicitement (Realtime peut être lent avec les filtres)
              loadMembres();
              loadEquipes();
            }}
            currentCount={membres.length}
            maxCount={limits.max_membres === -1 ? null : limits.max_membres}
          />

          {/* Modal d'édition de membres (simple ou multiple) (#3) */}
          <BulkMembreEditModal
            open={bulkEditModalOpen}
            onOpenChange={(open) => {
              setBulkEditModalOpen(open);
              if (!open) {
                setMembresToEdit([]);
                // NE PAS réinitialiser la sélection ici - seulement sur onSuccess
              }
            }}
            membres={membresToEdit}
            equipes={equipes}
            onSuccess={() => {
              loadMembres();
              loadEquipes();
              setSelectedMembres(new Set()); // Reset selection après édition réussie
            }}
          />

          {/* Modal d'affectation en masse (#2) */}
          {entrepriseId && (
            <ModalAffectationChantierBulk
              isOpen={bulkAffectationModalOpen}
              onClose={() => {
                setBulkAffectationModalOpen(false);
                // NE PAS réinitialiser la sélection ici - seulement sur onSuccess
              }}
              membres={membres.filter(m => selectedMembres.has(m.id))}
              entrepriseId={entrepriseId}
              onSuccess={() => {
                loadMembres();
                loadAffectationsActives();
                setSelectedMembres(new Set()); // Reset selection après affectation réussie
              }}
            />
          )}

          {/* Modal d'affectation bulk depuis "Changer statut → Sur chantier" (#6) */}
          {entrepriseId && (
            <ModalAffectationChantierBulk
              isOpen={bulkAffectationFromStatutOpen}
              onClose={() => {
                setBulkAffectationFromStatutOpen(false);
                // NE PAS réinitialiser la sélection ici
              }}
              membres={membresForBulkAffectation}
              entrepriseId={entrepriseId}
              onSuccess={() => {
                setBulkAffectationFromStatutOpen(false);
                setSelectedMembres(new Set());
                loadMembres();
                loadAffectationsActives();
              }}
            />
          )}

          {/* Modal de retrait bulk (#6) */}
          <ModalRetraitChantierBulk
            isOpen={retraitBulkModalOpen}
            onClose={() => {
              setRetraitBulkModalOpen(false);
              // NE PAS réinitialiser la sélection ici
            }}
            membres={membresForRetrait}
            affectationsActives={affectationsForRetraitBulk}
            nouveauStatut={nouveauStatutRetraitBulk}
            onSuccess={() => {
              setRetraitBulkModalOpen(false);
              setSelectedMembres(new Set());
              loadMembres();
              loadAffectationsActives();
            }}
          />
        </>
      )}

      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        onConfirm={confirmDelete}
        variant="delete"
      />

      {/* Modal de retrait du chantier */}
      <ModalRetraitChantier
        isOpen={modalRetraitOpen}
        onClose={() => {
          setModalRetraitOpen(false);
          setMembreARetirer(null);
          setAffectationsARetirer([]);
          // Réinitialiser le mode rotation si l'utilisateur annule
          if (isRotationModeRef.current) {
            setIsRotationMode(false);
            setMembreEnRotation(null);
            isRotationModeRef.current = false;
            membreEnRotationRef.current = null;
          }
        }}
        membre={membreARetirer}
        affectationsActives={affectationsARetirer}
        nouveauStatut={nouveauStatutRetrait as 'disponible' | 'repos' | 'en_arret' | 'en_rotation'}
        onConfirm={handleConfirmRetrait}
        isRotationMode={nouveauStatutRetrait === 'en_rotation'}
        chantiersDisponibles={chantiersDisponibles}
      />

      {/* Modal d'affectation au chantier */}
      <ModalAffectationChantier
        isOpen={modalAffectationOpen}
        onClose={() => {
          setModalAffectationOpen(false);
          setMembreAAfecter(null);
          setChantiersDisponibles([]);
          setAffectationsMembreActuel([]);
          // Réinitialiser le mode rotation si l'utilisateur annule
          if (isRotationModeRef.current) {
            setIsRotationMode(false);
            setMembreEnRotation(null);
            isRotationModeRef.current = false;
            membreEnRotationRef.current = null;
          }
        }}
        membre={membreAAfecter}
        chantiersDisponibles={chantiersDisponibles}
        affectationsExistantes={affectationsMembreActuel}
        onConfirm={handleConfirmAffectation}
      />

      {/* Modal de sélection de date de fin pour repos/arrêt */}
      <ModalDateFinStatut
        isOpen={modalDateFinStatutOpen}
        onClose={() => {
          setModalDateFinStatutOpen(false);
          setMembrePourDateFinStatut(null);
        }}
        membre={membrePourDateFinStatut}
        nouveauStatut={nouveauStatutPourDateFin}
        onConfirm={handleConfirmDateFinStatut}
      />

      {/* Modal de confirmation de changement anticipé */}
      <ModalConfirmChangementAnticipe
        isOpen={modalConfirmAnticipeOpen}
        onClose={() => {
          setModalConfirmAnticipeOpen(false);
          setMembrePourConfirmAnticipe(null);
        }}
        membre={membrePourConfirmAnticipe}
        ancienStatut={membrePourConfirmAnticipe?.statut || ''}
        nouveauStatut={nouveauStatutAnticipe}
        dateFinPrevue={membrePourConfirmAnticipe?.date_fin_statut || ''}
        onConfirm={handleConfirmChangementAnticipe}
      />

      {/* Modal fiche détail membre (#4) */}
      <MemberDetailModal
        membre={selectedMemberForDetail}
        open={memberDetailOpen}
        onOpenChange={(open) => {
          setMemberDetailOpen(open);
          if (!open) setSelectedMemberForDetail(null);
        }}
        equipeNom={selectedMemberForDetail?.equipe_id
          ? equipes.find(e => e.id === selectedMemberForDetail.equipe_id)?.nom
          : undefined
        }
        affectationActive={selectedMemberForDetail
          ? affectationsActives[selectedMemberForDetail.id]
          : undefined
        }
      />

      {/* Modal breakdown coût aujourd'hui (#1c) */}
      <ModalCoutJourBreakdown
        open={coutBreakdownModalOpen}
        onClose={() => setCoutBreakdownModalOpen(false)}
        breakdown={equipeStats.coutBreakdownAujourdhui}
        coutTotal={equipeStats.coutAujourdhui}
      />

      <div>
        <h1 className="text-3xl font-black text-gradient-primary flex items-center gap-2">
          <Users className="h-7 w-7 text-primary" aria-hidden="true" />
          Mon Équipe
        </h1>
        <p className="text-muted-foreground mt-1 text-base">
          Gérez vos salariés et leurs coûts
        </p>
      </div>

      {/* KPI Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {/* KPI 1: Disponibles X/Y - Cliquable pour filtrer */}
        <Card
          className={cn(
            "relative cursor-pointer transition-all duration-200 hover:ring-2 hover:ring-primary/30 hover:shadow-lg",
            filterStatut === 'disponible' && "ring-2 ring-primary shadow-lg"
          )}
          onClick={() => setFilterStatut(filterStatut === 'disponible' ? 'all' : 'disponible')}
        >
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-1 pt-3 px-4">
            <CardTitle className="text-xs font-medium text-white uppercase">
              Disponibles
            </CardTitle>
            <Users className="h-4 w-4 text-blue-500" />
          </CardHeader>
          <CardContent className="pb-9 px-4">
            <div className="text-2xl font-black text-white">
              {membresDisponibles}/{totalMembres}
            </div>
          </CardContent>
          <div className="absolute bottom-3 left-4 text-xs text-white/70 flex items-center gap-1">
            <ChevronRight className="h-3 w-3" />
            {filterStatut === 'disponible' ? 'Réinitialiser' : 'Filtrer disponibles'}
          </div>
          <HoverCard openDelay={200} closeDelay={100}>
            <HoverCardTrigger asChild>
              <button
                className="absolute bottom-3 right-3 p-1 rounded-full bg-muted/50 hover:bg-muted transition-colors cursor-help"
                type="button"
                onClick={(e) => e.stopPropagation()}
              >
                <Info className="h-3.5 w-3.5 text-muted-foreground" />
              </button>
            </HoverCardTrigger>
            <HoverCardContent side="top" align="end" sideOffset={8} className="w-64 p-3">
              <p className="text-sm text-foreground">Cliquez pour filtrer uniquement les membres disponibles. Cliquez à nouveau pour réinitialiser.</p>
            </HoverCardContent>
          </HoverCard>
        </Card>

        {/* KPI 2: Occupation % avec couleur dynamique - Cliquable pour filtrer */}
        <Card
          className={cn(
            "relative cursor-pointer transition-all duration-200 hover:ring-2 hover:ring-primary/30 hover:shadow-lg",
            filterStatut === 'sur_chantier' && "ring-2 ring-primary shadow-lg"
          )}
          onClick={() => setFilterStatut(filterStatut === 'sur_chantier' ? 'all' : 'sur_chantier')}
        >
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-1 pt-3 px-4">
            <CardTitle className="text-xs font-medium text-white uppercase">
              Sur chantier
            </CardTitle>
            <Activity className="h-4 w-4" style={{ color: equipeStats.occupationColor }} />
          </CardHeader>
          <CardContent className="pb-9 px-4">
            <div className="text-2xl font-black text-white">
              {equipeStats.tauxOccupation.toFixed(0)}%
            </div>
            <p className="text-xs text-muted-foreground mt-1">&nbsp;</p>
          </CardContent>
          <div className="absolute bottom-3 left-4 text-xs text-white/70 flex items-center gap-1">
            <ChevronRight className="h-3 w-3" />
            {filterStatut === 'sur_chantier' ? 'Réinitialiser' : 'Filtrer sur chantier'}
          </div>
          <HoverCard openDelay={200} closeDelay={100}>
            <HoverCardTrigger asChild>
              <button
                className="absolute bottom-3 right-3 p-1 rounded-full bg-muted/50 hover:bg-muted transition-colors cursor-help"
                type="button"
                onClick={(e) => e.stopPropagation()}
              >
                <Info className="h-3.5 w-3.5 text-muted-foreground" />
              </button>
            </HoverCardTrigger>
            <HoverCardContent side="top" align="end" sideOffset={8} className="w-64 p-3">
              <p className="text-sm text-foreground">Seuil optimal : ≥75%. Cliquez pour filtrer les membres sur chantier.</p>
            </HoverCardContent>
          </HoverCard>
        </Card>

        {/* KPI 3: Coût Aujourd'hui - Cliquable pour voir le breakdown */}
        <Card
          className="relative cursor-pointer transition-all duration-200 hover:ring-2 hover:ring-amber-500/30 hover:shadow-lg"
          onClick={() => setCoutBreakdownModalOpen(true)}
        >
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-1 pt-3 px-4">
            <CardTitle className="text-xs font-medium text-white uppercase">
              Coût du jour
            </CardTitle>
            <Coins className="h-4 w-4 text-amber-500" />
          </CardHeader>
          <CardContent className="pb-9 px-4">
            <div className="text-2xl font-black text-white">
              {new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(equipeStats.coutAujourdhui)}
            </div>
          </CardContent>
          <div className="absolute bottom-3 left-4 text-xs text-white/70 flex items-center gap-1">
            <ChevronRight className="h-3 w-3" />
            Voir le détail
          </div>
          <HoverCard openDelay={200} closeDelay={100}>
            <HoverCardTrigger asChild>
              <button
                className="absolute bottom-3 right-3 p-1 rounded-full bg-muted/50 hover:bg-muted transition-colors cursor-help"
                type="button"
                onClick={(e) => e.stopPropagation()}
              >
                <Info className="h-3.5 w-3.5 text-muted-foreground" />
              </button>
            </HoverCardTrigger>
            <HoverCardContent side="top" align="end" sideOffset={8} className="w-64 p-3">
              <p className="text-sm text-foreground">Cliquez pour voir le détail des coûts par membre actif aujourd'hui.</p>
            </HoverCardContent>
          </HoverCard>
        </Card>

        {/* KPI 4: Coût Période avec Switch */}
        <Card className="relative" data-miro-target="team-cout-semaine">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-1 pt-3 px-4">
            <CardTitle className="text-xs font-medium text-white uppercase">
              Coût {equipeStats.periodeMode === 'semaine' ? 'Semaine' : 'Mois'}
            </CardTitle>
            <CalendarDays className="h-4 w-4 text-violet-500" />
          </CardHeader>
          <CardContent className="pb-3 px-4">
            <div className="text-2xl font-black text-white">
              {new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(equipeStats.coutPeriode)}
            </div>
            {/* Switch Semaine/Mois */}
            <div className="flex items-center gap-2 mt-3">
              <span className={cn(
                "text-xs font-medium transition-colors",
                equipeStats.periodeMode === 'semaine' ? "text-violet-400" : "text-muted-foreground"
              )}>
                Sem.
              </span>
              <button
                onClick={() => equipeStats.setPeriodeMode(equipeStats.periodeMode === 'semaine' ? 'mois' : 'semaine')}
                className="relative w-9 h-5 bg-violet-500/30 rounded-full transition-colors flex-shrink-0"
              >
                <span className={cn(
                  "absolute top-0.5 left-0.5 w-4 h-4 bg-violet-400 rounded-full shadow transition-transform duration-200",
                  equipeStats.periodeMode === 'mois' ? "translate-x-4" : "translate-x-0"
                )} />
              </button>
              <span className={cn(
                "text-xs font-medium transition-colors",
                equipeStats.periodeMode === 'mois' ? "text-violet-400" : "text-muted-foreground"
              )}>
                Mois
              </span>
            </div>
          </CardContent>
          <HoverCard openDelay={200} closeDelay={100}>
            <HoverCardTrigger asChild>
              <button className="absolute bottom-3 right-3 p-1 rounded-full bg-muted/50 hover:bg-muted transition-colors cursor-help" type="button">
                <Info className="h-3.5 w-3.5 text-muted-foreground" />
              </button>
            </HoverCardTrigger>
            <HoverCardContent side="top" align="end" sideOffset={8} className="w-64 p-3">
              <p className="text-sm text-foreground">Projection du coût de main d'œuvre sur la période sélectionnée (5 jours travaillés/semaine, 22 jours travaillés/mois).</p>
            </HoverCardContent>
          </HoverCard>
        </Card>
      </div>

      {/* Filtres — masqués si < 10 membres */}
      {membres.length >= 10 && (
      <div className="flex gap-3 flex-wrap items-center bg-card/30 backdrop-blur-sm border border-border/50 rounded-lg px-4 py-3">
        <div className="flex items-center gap-2">
          <Label className="text-sm font-medium text-foreground/70">Statut:</Label>
          <Select
            value={filterStatut}
            onValueChange={(value: any) => setFilterStatut(value)}
          >
            <SelectTrigger className="w-[180px] bg-background/80 border-border/60 hover:border-border hover:bg-background transition-colors z-50">
              <SelectValue placeholder="Tous les statuts" />
            </SelectTrigger>
            <SelectContent className="bg-background z-50">
              <SelectItem value="all">Tous</SelectItem>
              <SelectItem value="sur_chantier">🚧 Sur chantier</SelectItem>
              <SelectItem value="disponible">✅ Disponibles</SelectItem>
              <SelectItem value="repos">😴 Repos</SelectItem>
              <SelectItem value="en_arret">🏥 En arrêt</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center gap-2">
          <Label className="text-sm font-medium text-foreground/70">Équipe:</Label>
          <Select
            value={filterEquipe || "all"}
            onValueChange={(value) => setFilterEquipe(value === "all" ? null : value)}
          >
            <SelectTrigger className="w-[180px] bg-background/80 border-border/60 hover:border-border hover:bg-background transition-colors z-50">
              <SelectValue placeholder="Toutes les équipes" />
            </SelectTrigger>
            <SelectContent className="bg-background z-[100]">
              <SelectItem value="all">Toutes les équipes</SelectItem>
              {equipes.map((equipe) => (
                <SelectItem key={equipe.id} value={equipe.id}>
                  {equipe.nom}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Filtre Métier/Spécialité (#3) */}
        <div className="flex items-center gap-2">
          <Label className="text-sm font-medium text-foreground/70">Métier:</Label>
          <Select
            value={filterSpecialite || "all"}
            onValueChange={(value) => setFilterSpecialite(value === "all" ? null : value)}
          >
            <SelectTrigger className="w-[180px] bg-background/80 border-border/60 hover:border-border hover:bg-background transition-colors z-50">
              <SelectValue placeholder="Tous les métiers" />
            </SelectTrigger>
            <SelectContent className="bg-background z-[100]">
              <SelectItem value="all">Tous les métiers</SelectItem>
              {specialitesUniques.map((specialite) => (
                <SelectItem key={specialite} value={specialite}>
                  {specialite}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Bouton Réinitialiser (#3) */}
        {hasActiveFilters && (
          <Button
            variant="ghost"
            size="sm"
            onClick={resetFilters}
            className="text-muted-foreground hover:text-foreground hover:bg-background/50"
          >
            <X className="h-4 w-4 mr-1" />
            Réinitialiser
          </Button>
        )}

      </div>
      )}

      <Card className="card-premium">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-xl font-black">Liste des membres</CardTitle>
              <CardDescription className="text-sm">
                {hasActiveFilters
                  ? `${filteredMembres.length} membre(s) sur ${membres.length}`
                  : `${membres.length} membre(s)`}
              </CardDescription>
            </div>

            {/* Boutons actions groupées - visibles si sélection */}
            {selectedMembres.size > 0 && (
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground mr-2">
                  {selectedMembres.size} sélectionné(s)
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleBulkEdit}
                >
                  <Pencil className="h-4 w-4 mr-1" />
                  Modifier
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setBulkEquipeDialogOpen(true)}
                >
                  <Users className="h-4 w-4 mr-1" />
                  Changer équipe
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setBulkStatutDialogOpen(true)}
                >
                  <RefreshCw className="h-4 w-4 mr-1" />
                  Changer statut
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setBulkAffectationModalOpen(true)}
                >
                  <Briefcase className="h-4 w-4 mr-1" />
                  Affecter au chantier
                </Button>
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent className="pt-2">
          <div className="overflow-x-auto">
            <Table className="text-sm">
              <TableHeader>
                <TableRow className="h-9">
                  <TableHead className="w-10 py-2" data-miro-target="team-bulk-checkbox">
                    <Checkbox
                      checked={selectedMembres.size === filteredMembres.length && filteredMembres.length > 0}
                      onCheckedChange={(checked) => {
                        if (checked) {
                          setSelectedMembres(new Set(filteredMembres.map(m => m.id)));
                        } else {
                          setSelectedMembres(new Set());
                        }
                      }}
                    />
                  </TableHead>
                  <TableHead className="py-2">
                    <SortableHeader column="nom" label={tables.team.columns[0]} />
                  </TableHead>
                  <TableHead className="py-2">
                    <SortableHeader column="poste" label={tables.team.columns[1]} />
                  </TableHead>
                  <TableHead className="font-bold py-2">Équipe</TableHead>
                  {false && <TableHead className="py-2">
                    <SortableHeader column="specialite" label={tables.team.columns[2]} />
                  </TableHead>}
                  {false && <TableHead className="text-right py-2">
                    <SortableHeader column="taux_horaire" label={tables.team.columns[3]} align="right" />
                  </TableHead>}
                  <TableHead className="text-right py-2">
                    <SortableHeader column="cout_journalier" label={tables.team.columns[5]} align="right" />
                  </TableHead>
                  <TableHead className="py-2">
                    <SortableHeader column="statut" label={tables.team.columns[6]} />
                  </TableHead>
                  <TableHead className="py-2 font-bold" data-miro-target="team-affectation-header">Chantier</TableHead>
                  <TableHead className="text-right font-bold py-2">{tables.team.columns[7]}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredMembres.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-10">
                      <EmptyState
                        icon={Users}
                        title="Aucun membre pour le moment"
                        text="Ajoutez vos premiers salariés pour pouvoir les affecter aux chantiers"
                        primaryAction={{
                          label: "Ajouter un membre",
                          onClick: () => setBulkAddMemberOpen(true),
                        }}
                      />
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredMembres.map((membre) => (
                    <TableRow key={membre.id} className="hover:bg-muted/50 transition-colors h-10">
                      <TableCell className="w-10 py-2">
                        <Checkbox
                          checked={selectedMembres.has(membre.id)}
                          onCheckedChange={(checked) => {
                            const newSet = new Set(selectedMembres);
                            if (checked) {
                              newSet.add(membre.id);
                            } else {
                              newSet.delete(membre.id);
                            }
                            setSelectedMembres(newSet);
                          }}
                        />
                      </TableCell>
                      <TableCell className="font-bold py-2">
                        <HoverCard openDelay={300} closeDelay={100}>
                          <HoverCardTrigger asChild>
                            <span
                              className="cursor-pointer underline decoration-amber-500/40 text-white hover:text-primary hover:decoration-primary transition-colors"
                              onClick={() => {
                                setSelectedMemberForDetail(membre);
                                setMemberDetailOpen(true);
                              }}
                            >
                              {membre.prenom} {membre.nom}
                            </span>
                          </HoverCardTrigger>
                          <HoverCardContent
                            side="right"
                            align="start"
                            className="w-72 p-4 bg-zinc-900 border-2 border-primary/50 shadow-2xl shadow-black/70 rounded-xl cursor-pointer hover:border-primary transition-colors"
                            onClick={() => {
                              setSelectedMemberForDetail(membre);
                              setMemberDetailOpen(true);
                            }}
                          >
                            <div className="space-y-3">
                              {/* En-tête avec nom et statut */}
                              <div className="flex items-center justify-between pb-2 border-b border-primary/20">
                                <span className="font-bold text-sm text-white">{membre.prenom} {membre.nom}</span>
                                <Badge className={cn("text-[10px] py-0.5 px-2", getStatutBadge(getStatutReel(membre)).className)}>
                                  {getStatutBadge(getStatutReel(membre)).label}
                                </Badge>
                              </div>

                              {/* Dernière affectation */}
                              <div className="space-y-2">
                                <span className="text-zinc-400 text-[10px] uppercase tracking-wide">Dernier chantier</span>
                                {dernieresAffectations[membre.id] ? (
                                  <div className="bg-zinc-800/70 p-3 rounded-lg space-y-2">
                                    <div className="flex items-center justify-between">
                                      <span className="font-bold text-white text-sm">
                                        {dernieresAffectations[membre.id].chantier}
                                      </span>
                                      <Badge className={cn(
                                        "text-[9px] py-0 px-1.5",
                                        dernieresAffectations[membre.id].isEnCours
                                          ? "bg-green-600 text-white"
                                          : "bg-zinc-600 text-zinc-200"
                                      )}>
                                        {dernieresAffectations[membre.id].isEnCours ? '🔄 En cours' : '✓ Terminée'}
                                      </Badge>
                                    </div>
                                    <div className="flex items-center gap-2 text-xs text-zinc-300">
                                      <CalendarDays className="h-3 w-3" />
                                      <span>
                                        {new Date(dernieresAffectations[membre.id].dateDebut).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' })}
                                        {' → '}
                                        {new Date(dernieresAffectations[membre.id].dateFin).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' })}
                                      </span>
                                      <span className="text-zinc-500">•</span>
                                      <span className="font-bold text-sky-400">
                                        {dernieresAffectations[membre.id].jours}j
                                      </span>
                                    </div>
                                  </div>
                                ) : (
                                  <div className="bg-zinc-800/50 p-3 rounded-lg text-center">
                                    <span className="text-zinc-500 text-xs">Aucun chantier</span>
                                  </div>
                                )}
                              </div>
                            </div>

                            {/* Call-to-action */}
                            <div className="mt-3 pt-3 border-t border-primary/20">
                              <p className="text-[11px] text-white font-bold text-center">
                                👆 Cliquer pour voir l'historique complet
                              </p>
                            </div>
                          </HoverCardContent>
                        </HoverCard>
                      </TableCell>
                      <TableCell className="py-2">{membre.poste}</TableCell>
                      <TableCell className="py-2">
                        {entrepriseId && (
                          <MemberTeamAssignment
                            membreId={membre.id}
                            currentEquipeId={membre.equipe_id}
                            entrepriseId={entrepriseId}
                            onUpdate={loadMembres}
                          />
                        )}
                      </TableCell>
                      {false && <TableCell className="py-2">{membre.specialite}</TableCell>}
                      {false && <TableCell className="text-right font-mono font-semibold py-2">{membre.taux_horaire.toFixed(2)} €</TableCell>}
                      <TableCell className="text-right font-mono font-black text-sky-400 py-2">
                        {calculerCoutJournalierMembre(membre).toFixed(2)} €
                      </TableCell>
                      <TableCell className="py-2">
                        <Select
                          value={getStatutReel(membre)}
                          onValueChange={(value) => handleStatutChange(membre.id, value as Membre["statut"])}
                        >
                          <SelectTrigger className="w-[150px] h-8 text-xs bg-muted/60">
                            <SelectValue>{getStatutBadge(getStatutReel(membre)).label}</SelectValue>
                          </SelectTrigger>
                          <SelectContent className="bg-background z-50">
                            <SelectItem value="sur_chantier">🚧 Sur chantier</SelectItem>
                            <SelectItem value="disponible">✅ Disponible</SelectItem>
                            {getStatutReel(membre) === 'sur_chantier' && (
                              <SelectItem value="en_rotation">🔄 En rotation</SelectItem>
                            )}
                            <SelectItem value="repos">😴 Repos</SelectItem>
                            <SelectItem value="en_arret">🏥 En arrêt</SelectItem>
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell className="py-2 text-sm">
                        {/* Affectation active - afficher dès qu'une affectation existe */}
                        {(membre.statut === 'repos' || membre.statut === 'en_arret') && membre.date_fin_statut ? (
                          <span className="text-warning">
                            → {new Date(membre.date_fin_statut).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' })}
                          </span>
                        ) : affectationsActives[membre.id] ? (
                          <div className="flex flex-col gap-0.5">
                            {/* Orange pour retard, vert clair pour chantier normal */}
                            <span className={`font-medium text-xs ${affectationsActives[membre.id].isRetard ? 'text-amber-500' : 'text-white'}`}>
                              {affectationsActives[membre.id].chantier}
                            </span>
                            <span className="text-zinc-400 text-xs font-medium">
                              → {new Date(affectationsActives[membre.id].dateFin).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' })}
                            </span>
                          </div>
                        ) : (
                          <span className="text-muted-foreground/50">—</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right py-2">
                        <div className="flex justify-end gap-1">
                          <HoverCard openDelay={300} closeDelay={100}>
                            <HoverCardTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => {
                                  setSelectedMemberForDetail(membre);
                                  setMemberDetailOpen(true);
                                }}
                                className="hover:bg-amber-500/10 h-7 w-7"
                                aria-label="Voir les affectations"
                                title="Voir les affectations"
                              >
                                <CalendarDays className="h-3.5 w-3.5 text-amber-500" aria-hidden="true" />
                              </Button>
                            </HoverCardTrigger>
                            <HoverCardContent
                              side="left"
                              align="start"
                              className="w-72 p-4 bg-zinc-900 border-2 border-primary/50 shadow-2xl shadow-black/70 rounded-xl cursor-pointer hover:border-primary transition-colors"
                              onClick={() => {
                                setSelectedMemberForDetail(membre);
                                setMemberDetailOpen(true);
                              }}
                            >
                              <div className="space-y-3">
                                <div className="flex items-center justify-between pb-2 border-b border-primary/20">
                                  <span className="font-bold text-sm text-white">{membre.prenom} {membre.nom}</span>
                                  <Badge className={cn("text-[10px] py-0.5 px-2", getStatutBadge(getStatutReel(membre)).className)}>
                                    {getStatutBadge(getStatutReel(membre)).label}
                                  </Badge>
                                </div>
                                <div className="space-y-2">
                                  <span className="text-zinc-400 text-[10px] uppercase tracking-wide">Dernier chantier</span>
                                  {dernieresAffectations[membre.id] ? (
                                    <div className="bg-zinc-800/70 p-3 rounded-lg space-y-2">
                                      <div className="flex items-center justify-between">
                                        <span className="font-bold text-white text-sm">
                                          {dernieresAffectations[membre.id].chantier}
                                        </span>
                                        <Badge className={cn(
                                          "text-[9px] py-0 px-1.5",
                                          dernieresAffectations[membre.id].isEnCours
                                            ? "bg-green-600 text-white"
                                            : "bg-zinc-600 text-zinc-200"
                                        )}>
                                          {dernieresAffectations[membre.id].isEnCours ? '🔄 En cours' : '✓ Terminée'}
                                        </Badge>
                                      </div>
                                      <div className="flex items-center gap-2 text-xs text-zinc-300">
                                        <CalendarDays className="h-3 w-3" />
                                        <span>
                                          {new Date(dernieresAffectations[membre.id].dateDebut).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' })}
                                          {' → '}
                                          {new Date(dernieresAffectations[membre.id].dateFin).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' })}
                                        </span>
                                        <span className="text-zinc-500">•</span>
                                        <span className="font-bold text-sky-400">
                                          {dernieresAffectations[membre.id].jours}j
                                        </span>
                                      </div>
                                    </div>
                                  ) : (
                                    <div className="bg-zinc-800/50 p-3 rounded-lg text-center">
                                      <span className="text-zinc-500 text-xs">Aucun chantier</span>
                                    </div>
                                  )}
                                </div>
                              </div>
                              <div className="mt-3 pt-3 border-t border-primary/20">
                                <p className="text-[11px] text-white font-bold text-center">
                                  👆 Cliquer pour voir l'historique complet
                                </p>
                              </div>
                            </HoverCardContent>
                          </HoverCard>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleEdit(membre)}
                            className="hover:bg-primary/10 h-7 w-7"
                            aria-label={labels.actions.edit}
                            title={labels.actions.edit}
                          >
                            <Pencil className="h-3.5 w-3.5" aria-hidden="true" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDelete(membre.id)}
                            className="hover:bg-danger/10 h-7 w-7"
                            aria-label={labels.actions.delete}
                            title={labels.actions.delete}
                          >
                            <Trash2 className="h-3.5 w-3.5 text-danger" aria-hidden="true" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Dialog changement équipe groupé */}
      <Dialog open={bulkEquipeDialogOpen} onOpenChange={setBulkEquipeDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Changer l'équipe</DialogTitle>
            <DialogDescription>
              Assigner {selectedMembres.size} membre(s) à une équipe
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-3 py-4">
            <Button variant="outline" onClick={() => handleBulkEquipeChange(null)}>
              Aucune équipe
            </Button>
            {equipes.map((equipe) => (
              <Button
                key={equipe.id}
                variant="outline"
                onClick={() => handleBulkEquipeChange(equipe.id)}
              >
                {equipe.nom}
              </Button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog changement statut groupé */}
      <Dialog open={bulkStatutDialogOpen} onOpenChange={setBulkStatutDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Changer le statut</DialogTitle>
            <DialogDescription>
              Modifier le statut de {selectedMembres.size} membre(s)
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-3 py-4">
            <Button variant="outline" onClick={() => handleBulkStatutChange('disponible')}>
              ✅ Disponible
            </Button>
            <Button variant="outline" onClick={() => handleBulkStatutChange('sur_chantier')}>
              🚧 Sur chantier
            </Button>
            <Button variant="outline" onClick={() => handleBulkStatutChange('repos')}>
              🏠 Repos
            </Button>
            <Button variant="outline" onClick={() => handleBulkStatutChange('en_arret')}>
              🏥 En arrêt
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* TipsModal - Conseils depuis la checklist */}
      <TipsModal isOpen={showTips} onClose={handleCloseTips} title="Organiser votre équipe">
        <div className="space-y-4">
          <p className="font-medium">Voici comment bien démarrer :</p>
          <ol className="space-y-3 list-decimal list-inside">
            <li><strong>Créez plusieurs équipes d'un coup</strong> avec le bouton "+ Nouvelle équipe"</li>
            <li><strong>Ajoutez vos salariés en masse</strong> — utilisez les templates rapides (Ouvrier, Chef, Conducteur)</li>
            <li><strong>Dupliquez une ligne</strong> pour copier les paramètres et gagner du temps</li>
            <li><strong>Assignez-les à une équipe</strong> pour les affecter facilement aux chantiers</li>
          </ol>
          <p className="text-sm mt-2 opacity-70">💡 Vos brouillons sont sauvegardés automatiquement !</p>
        </div>
      </TipsModal>

      {/* Annotations Miro Team */}
      <MiroAnnotations
        pageId="team"
        annotations={[
          {
            id: "team-cout-semaine",
            targetSelector: "[data-miro-target='team-cout-semaine']",
            text: "Basculez entre coût semaine et coût mois pour estimer vos charges salariales",
            position: "left",
            offsetX: -20,
            offsetY: 30,
          },
          {
            id: "team-bulk-checkbox",
            targetSelector: "[data-miro-target='team-bulk-checkbox']",
            text: "Cochez plusieurs membres pour les modifier, affecter ou changer de statut en lot",
            position: "right",
            offsetX: 30,
            offsetY: 0,
          },
          {
            id: "team-affectation-header",
            targetSelector: "[data-miro-target='team-affectation-header']",
            text: "Le chantier sur lequel ce membre travaille actuellement. Cliquez sur son nom pour voir son historique",
            position: "top",
            offsetX: 0,
            offsetY: -20,
          },
        ]}
      />
    </div>
  );
};

export default Team;
